// src/admin/components/access-urls/ShortUrlBatchCard.jsx
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CollapsibleCard from '../common/CollapsibleCard';
import { useBatchShortUrls, useShortUrlActions, useShortUrlPdfExports } from '../../hooks/useShortUrlBatches';
import useAdminWorkflows from '../../hooks/useAdminWorkflows';
import ClaimUrlModal from './ClaimUrlModal';
import ShortUrlCodeDisplay from './ShortUrlCodeDisplay';
import { WorkflowTypeBadges } from '../../../shared/components/WorkflowTypeBadge';
import RedeemableCodeStatusBadge, {
  redeemableCodeStatusLabel,
} from '../../../shared/components/RedeemableCodeStatusBadge';
import AdminModal from '../common/AdminModal';
import DataTable from '../common/DataTable';
import PersonNameDisplay from '../common/PersonNameDisplay';
import { formatDate } from '../../../shared/utils/formatDate';

/**
 * Card for displaying a single batch with expandable short URLs table
 */
export default function ShortUrlBatchCard({ batch, onExport, onExportBatchPdf, onBatchCountsChange }) {
  const { shortUrls, loading, loaded, fetchShortUrls, refresh } = useBatchShortUrls(batch.batch_number);
  const { claimUrl, setUrlWorkflow, invalidateUrl } = useShortUrlActions();
  const { startSinglePdfExport, waitForPdfExportAndDownload } = useShortUrlPdfExports();
  const { data: workflows } = useAdminWorkflows();

  // Modal state for claiming
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState(null);

  // Modal state for editing the workflow on a row
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowEditUrl, setWorkflowEditUrl] = useState(null);
  const [workflowDraftId, setWorkflowDraftId] = useState('');
  const [workflowSubmitting, setWorkflowSubmitting] = useState(false);
  const [batchPdfLoading, setBatchPdfLoading] = useState(false);
  const [rowPdfLoadingId, setRowPdfLoadingId] = useState(null);

  const userWorkflowOptions = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state === 'published'),
    [workflows]
  );
  const draftUserWorkflowCount = useMemo(
    () => (workflows || []).filter((w) => w.user_workflow && w.state !== 'published').length,
    [workflows]
  );

  // Handle expand - fetch short URLs if not already loaded
  const handleExpand = useCallback(() => {
    if (!loaded) {
      fetchShortUrls();
    }
  }, [loaded, fetchShortUrls]);

  const handleClaim = useCallback((row) => {
    setSelectedUrl(row);
    setClaimModalOpen(true);
  }, []);

  // Handle claim completion - only refreshes the list inside this card
  const handleClaimComplete = async (assetId, workflowId) => {
    try {
      await claimUrl(selectedUrl.id, assetId, { workflowId });
      setClaimModalOpen(false);
      setSelectedUrl(null);
      refresh();
      if (onBatchCountsChange) onBatchCountsChange();
    } catch (err) {
      alert(err.message || 'Failed to claim URL');
    }
  };

  const handleInvalidate = useCallback(async (row) => {
    const action = row['claimed?'] ? 'invalidate' : 'delete';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this short URL (${row.code})? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await invalidateUrl(row.id);
      refresh();
      if (onBatchCountsChange) onBatchCountsChange();
    } catch (err) {
      alert(err.message || `Failed to ${action} URL`);
    }
  }, [invalidateUrl, refresh, onBatchCountsChange]);

  const openWorkflowEditor = useCallback((row) => {
    setWorkflowEditUrl(row);
    setWorkflowDraftId(row.workflow_id ? String(row.workflow_id) : '');
    setWorkflowModalOpen(true);
  }, []);

  const closeWorkflowEditor = () => {
    setWorkflowModalOpen(false);
    setWorkflowEditUrl(null);
    setWorkflowDraftId('');
  };

  const submitWorkflowEditor = async () => {
    if (!workflowEditUrl) return;
    setWorkflowSubmitting(true);
    try {
      await setUrlWorkflow(workflowEditUrl.id, workflowDraftId || null);
      closeWorkflowEditor();
      refresh();
    } catch (err) {
      alert(err.message || 'Failed to update workflow');
    } finally {
      setWorkflowSubmitting(false);
    }
  };

  const handleBatchPdfExport = async (e) => {
    e.stopPropagation();
    if (!onExportBatchPdf) return;
    setBatchPdfLoading(true);
    try {
      await onExportBatchPdf(batch.batch_number);
    } catch (err) {
      alert(err.message || 'Failed to generate batch PDF');
    } finally {
      setBatchPdfLoading(false);
    }
  };

  const handleSinglePdfExport = useCallback(async (row) => {
    setRowPdfLoadingId(row.id);
    try {
      const created = await startSinglePdfExport(batch.batch_number, row.id);
      await waitForPdfExportAndDownload(created.id);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      alert(err.message || 'Failed to generate single-label PDF');
    } finally {
      setRowPdfLoadingId(null);
    }
  }, [batch.batch_number, startSinglePdfExport, waitForPdfExportAndDownload]);

  // Get actions for a specific row
  const getActionsForRow = useCallback((row) => {
    const actions = [];

    // Claim action (only for unclaimed, non-invalidated)
    if (!row['claimed?'] && !row['invalidated?']) {
      actions.push({
        key: 'claim',
        label: 'Claim',
        icon: 'bi-link-45deg',
        onClick: () => handleClaim(row),
      });
    }

    // Workflow edit (claimed and non-invalidated). Always offer the action when the
    // row already has a workflow assigned (so it can be cleared) or there are User
    // Workflows to surface — published options to pick, or drafts to warn about.
    const canEditWorkflow =
      row['claimed?'] &&
      !row['invalidated?'] &&
      (row.workflow || userWorkflowOptions.length > 0 || draftUserWorkflowCount > 0);
    if (canEditWorkflow) {
      actions.push({
        key: 'workflow',
        label: row.workflow ? 'Change workflow' : 'Set workflow',
        icon: 'bi-diagram-2',
        onClick: () => openWorkflowEditor(row),
      });
    }

    // Invalidate action (for any non-invalidated URL)
    if (!row['invalidated?']) {
      actions.push({
        key: 'invalidate',
        label: row['claimed?'] ? 'Invalidate' : 'Delete',
        icon: 'bi-trash',
        onClick: () => handleInvalidate(row),
      });
    }

    actions.push({
      key: 'download-pdf',
      label: rowPdfLoadingId === row.id ? 'Generating PDF…' : 'Download label PDF',
      icon: 'bi-file-earmark-pdf',
      onClick: () => handleSinglePdfExport(row),
    });

    return actions;
  }, [
    userWorkflowOptions,
    draftUserWorkflowCount,
    rowPdfLoadingId,
    handleClaim,
    openWorkflowEditor,
    handleInvalidate,
    handleSinglePdfExport,
  ]);

  // Custom render for actions dropdown
  const renderActionsCell = useCallback((row) => {
    const rowActions = getActionsForRow(row);
    if (rowActions.length === 0) return <span className="text-muted">—</span>;

    return (
      <div className="dropdown">
        <button
          className="btn btn-link p-0 text-muted"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title="Batch actions"
          aria-label="Batch actions"
        >
          <i className="bi bi-three-dots-vertical" />
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          {rowActions.map(action => (
            <li key={action.key}>
              <button
                className="dropdown-item"
                onClick={action.onClick}
              >
                <i className={`bi ${action.icon} me-2`} />
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }, [getActionsForRow]);

  const shortUrlColumns = useMemo(
    () => [
      {
        key: 'code',
        label: 'Short URL',
        sortable: true,
        render: (row) => (
          <ShortUrlCodeDisplay
            code={row.code}
            claimed={row['claimed?']}
            invalidated={row['invalidated?']}
            accessKeyToken={row.access_key}
          />
        ),
        filterValue: (row) => row.code || '',
      },
      {
        key: 'asset',
        label: 'Asset',
        sortable: true,
        render: (row) =>
          row.asset?.id ? (
            <Link to={`/admin/assets/${row.asset.id}`} className="text-decoration-none">
              {row.asset.asset_name || 'Unnamed Asset'}
            </Link>
          ) : (
            <span className="text-muted">—</span>
          ),
        filterValue: (row) => row.asset?.asset_name || '',
      },
      {
        key: 'workflow',
        label: 'Workflow',
        sortable: true,
        render: (row) =>
          row.workflow ? (
            <span className="d-inline-flex align-items-center gap-2">
              <span className="text-body">{row.workflow.name}</span>
              <WorkflowTypeBadges workflow={row.workflow} />
            </span>
          ) : (
            <span className="text-muted fst-italic small">All self-startable</span>
          ),
        filterValue: (row) => row.workflow?.name || 'all self-startable',
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => (
          <RedeemableCodeStatusBadge
            domain="short_url"
            invalidated={row['invalidated?']}
            consumed={row['claimed?']}
          />
        ),
        filterValue: (row) =>
          redeemableCodeStatusLabel('short_url', row['invalidated?'], row['claimed?']),
      },
      {
        key: 'row_actions',
        label: '',
        className: 'text-end',
        style: { width: 80 },
        hidden: true,
        render: (row) => renderActionsCell(row),
      },
    ],
    [renderActionsCell]
  );

  // Render header content.
  //
  // Three stacked rows rather than two columns: name and id, then who and when, then the two
  // exports. The buttons used to sit in a right-hand column, which on a narrow screen squeezed
  // the name to a couple of characters and still wrapped. Below `md` they take a row of their
  // own under the metadata and stretch to the full width, which is also an easier tap target;
  // above it they return to the right of the first row, where they were.
  const exportButtons = (
    <>
      <button
        className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-md-grow-0"
        onClick={(e) => {
          e.stopPropagation();
          onExport(batch.batch_number);
        }}
      >
        <i className="bi bi-download" />
        Export CSV
      </button>
      <button
        className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center gap-1 flex-grow-1 flex-md-grow-0"
        onClick={handleBatchPdfExport}
        disabled={batchPdfLoading}
      >
        {batchPdfLoading ? (
          <>
            <span className="spinner-border spinner-border-sm" role="status" />
            Generating…
          </>
        ) : (
          <>
            <i className="bi bi-file-earmark-pdf" />
            Download Batch PDF
          </>
        )}
      </button>
    </>
  );

  const header = (
    <div className="w-100 pe-2">
      <div className="d-flex flex-column gap-1">
        {/* Row 1: Batch name, with the id pushed to the far right. */}
        <div className="d-flex align-items-baseline justify-content-between gap-2">
          <span className="fw-semibold text-truncate">{batch.name || 'Unnamed Batch'}</span>
          <div className="d-flex align-items-baseline gap-2 flex-shrink-0">
            <span className="text-muted small text-nowrap">{batch.batch_number}</span>
            {/* Above `md` the exports stay on this row. */}
            <div className="d-none d-md-flex align-items-center gap-2 ms-2">
              {exportButtons}
            </div>
          </div>
        </div>
        {/* Row 2: User, date, URL count */}
        <div className="text-muted small d-flex align-items-center flex-wrap gap-3">
          <span className="d-inline-flex align-items-center gap-1">
            <i className="bi bi-person" />
            <PersonNameDisplay
              givenName={batch.generated_by?.given_name}
              familyName={batch.generated_by?.family_name}
              email={batch.generated_by?.email}
              variant="inline"
            />
          </span>
          <span>
            <i className="bi bi-calendar me-1" />
            {formatDate(batch.created_at)}
          </span>
          <span className="text-primary fw-medium">
            {batch.counts?.total || 0} URLs
          </span>
        </div>
        {/* Row 3, below `md` only: the exports, full width. */}
        <div className="d-flex d-md-none align-items-center gap-2 mt-1">
          {exportButtons}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <CollapsibleCard
        header={header}
        onExpand={handleExpand}
        isLoading={loading}
        isEmpty={loaded && shortUrls.length === 0}
        emptyMessage="No short URLs in this batch."
        variant="table"
      >
        <div className="table-responsive px-0">
          <DataTable
            items={shortUrls}
            columns={shortUrlColumns}
            filterPlaceholder="Search URLs in this batch…"
            emptyMessage="No short URLs in this batch."
            hideFilter={shortUrls.length < 5}
            getRowKey={(row) => row.id}
            className="mb-0"
          />
        </div>
      </CollapsibleCard>

      {/* Claim Modal */}
      <ClaimUrlModal
        show={claimModalOpen}
        onClose={() => {
          setClaimModalOpen(false);
          setSelectedUrl(null);
        }}
        shortUrl={selectedUrl}
        onClaim={handleClaimComplete}
      />

      {/* Workflow editor */}
      <AdminModal
        show={workflowModalOpen}
        onClose={closeWorkflowEditor}
        title="Set direct-launch workflow"
        footer={
          <div className="d-flex justify-content-end gap-2 w-100">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeWorkflowEditor}
              disabled={workflowSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitWorkflowEditor}
              disabled={workflowSubmitting}
            >
              {workflowSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        }
      >
        <p className="text-muted mb-3">
          Choose a User Workflow to launch automatically when this short URL is
          scanned, or clear the selection to revert to a regular asset access link.
        </p>
        <select
          className="form-select"
          value={workflowDraftId}
          onChange={(e) => setWorkflowDraftId(e.target.value)}
        >
          <option value="">— None (regular asset access link) —</option>
          {userWorkflowOptions.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
        {draftUserWorkflowCount > 0 && (
          <div className="alert alert-warning mt-3 mb-0 py-2 small">
            <i className="bi bi-exclamation-triangle me-2" />
            {draftUserWorkflowCount === 1
              ? '1 User Workflow is still in draft and not selectable here. Publish it to use it as a direct-launch workflow.'
              : `${draftUserWorkflowCount} User Workflows are still in draft and not selectable here. Publish them to use them as direct-launch workflows.`}
          </div>
        )}
      </AdminModal>
    </>
  );
}

ShortUrlBatchCard.propTypes = {
  batch: PropTypes.shape({
    id: PropTypes.number,
    batch_number: PropTypes.string.isRequired,
    name: PropTypes.string,
    created_at: PropTypes.string,
    generated_by: PropTypes.object,
    counts: PropTypes.shape({
      total: PropTypes.number,
      claimed: PropTypes.number,
      unclaimed: PropTypes.number,
      invalidated: PropTypes.number,
    }),
  }).isRequired,
  onExport: PropTypes.func.isRequired,
  onExportBatchPdf: PropTypes.func,
  onBatchCountsChange: PropTypes.func,
};
